import React, { useEffect, useState, useContext, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar, faClock, faMotorcycle, faCircle, faPlay, faPlus, faMinus } from "@fortawesome/free-solid-svg-icons";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import MyContext from "../Utils/MyContext";
import { 
  setCartItems, 
  setResDetails, 
  clearCart,
  setCartLoading,
  setCartError,
  updateItemQuantity,
  addItems
} from "../Utils/cartSlice";
import ResetCart from "./ResetCart";
import RestaurantMenuShimmering from "./RestaurantMenuShimmering";
import { RESTAURANT_SHOW_API_URL, RESTAURANT_MENU_ITEMS_API_URL, CART_API_URL } from "../helpers/Constant";
import { authService } from "../services/authService";
import { cartService } from "../services/cartService";

// Toast notification component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-green-500' : 'bg-red-500';
  const icon = type === 'success' ? '✅' : '❌';

  return (
    <div className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg z-50 ${bgColor} text-white animate-slide-in max-w-sm`}>
      <div className="flex items-center gap-2">
        <span>{icon}</span>
        <span>{message}</span>
      </div>
    </div>
  );
};

const RestaurantMenu = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const cartItems = useSelector((store) => store.cart.cartItems);
  const resDetails = useSelector((store) => store.cart.resDetails);
  
  // New state for separated data
  const [restaurantInfo, setRestaurantInfo] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [menuPagination, setMenuPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    perPage: 20,
    total: 0,
  });
  const [loading, setLoading] = useState(true);
  const [menuLoading, setMenuLoading] = useState(false);
  const [error, setError] = useState(null);
  const [menuError, setMenuError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [resCartAlert, setResCartAlert] = useState(false);
  const [resetCartShown, setResetCartShown] = useState(false);
  const [loadingItems, setLoadingItems] = useState({});
  const [toast, setToast] = useState(null);
  const [isNetworkError, setIsNetworkError] = useState(false);
  
  const locDetails = useSelector((store) => store.location.locationDetails);
  const city = locDetails?.[0]?.district || "";
  const authToken = authService.getToken();

  // Toast helper
  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  const hideToast = useCallback(() => {
    setToast(null);
  }, []);

  const contextValue = {
    resCartAlert,
    showResCartAlert: () => {
      setResCartAlert(true);
      setResetCartShown(true);
    },
    hideResCartAlert: () => {
      setResCartAlert(false);
      setResetCartShown(false);
    },
    handleResetCart: async () => {
      try {
        dispatch(setCartLoading(true));
        const result = await cartService.clearCart();
        if (result.success) {
          dispatch(clearCart());
          contextValue.hideResCartAlert();
          showToast('Cart cleared successfully', 'success');
        } else {
          showToast('Failed to clear cart', 'error');
        }
      } catch (error) {
        console.error("Error resetting cart:", error);
        dispatch(setCartError("Failed to reset cart"));
        showToast('Failed to reset cart', 'error');
      } finally {
        dispatch(setCartLoading(false));
      }
    },
  };

  // 1. Load restaurant details (POST) with payload { "id": restaurant_id }
  const loadRestaurantInfo = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setIsNetworkError(false);

      const url = RESTAURANT_SHOW_API_URL;
      console.log("📡 Fetching restaurant info from:", url);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({ id: parseInt(id) }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("📦 Restaurant Info Response:", result);

      if (result?.status === true && result?.status_code === 200 && result?.data) {
        const data = result.data;
        const restaurant = data.restaurant;

        // Process categories: add default image if missing
        const categories = (data.categories || []).map(cat => ({
          ...cat,
          image: cat.image && cat.image.trim() !== "" 
            ? cat.image 
            : "https://staging.jagods.in/frontend/images/default/category.png"
        }));

        setRestaurantInfo({
          ...restaurant,
          categories: categories,
          reviews: data.reviews || [],
          avgRating: data.avgRating || 0,
          countUser: data.countUser || 0,
        });

        // Update Redux with restaurant details
        dispatch(setResDetails({
          id: restaurant.id,
          resName: restaurant.name,
          resArea: restaurant.address || "",
          resImg: restaurant.image || "",
          resSla: {
            deliveryTime: restaurant.estimated_delivery_time || "30-40 mins",
          },
        }));
      } else {
        setError(result?.message || "Failed to fetch restaurant details");
      }
    } catch (err) {
      console.error("❌ Error fetching restaurant info:", err);
      if (err.message.includes("Failed to fetch") || err.message.includes("Network")) {
        setIsNetworkError(true);
        setError("Network error. Please check your internet connection and try again.");
      } else {
        setError("Something went wrong. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  }, [id, dispatch]);

  // 2. Load menu items (with pagination) – POST
  const loadMenuItems = useCallback(async (page = 1, sortBy = "popularity") => {
    if (!id) return;
    try {
      setMenuLoading(true);
      setMenuError(null);

      const url = RESTAURANT_MENU_ITEMS_API_URL;
      const payload = {
        restaurant_id: parseInt(id),
        sort_by: sortBy,
        per_page: 20,
        page: page,
      };

      console.log("📡 Fetching menu items page", page, "from:", url, payload);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log("📦 Menu Items Response:", result);

      if (result?.status === true && result?.status_code === 200 && result?.data) {
        const data = result.data;
        // Append new items if page > 1, else replace
        setMenuItems(prev => page === 1 ? data.data : [...prev, ...data.data]);
        setMenuPagination({
          currentPage: data.current_page,
          lastPage: data.last_page,
          perPage: data.per_page,
          total: data.total,
        });
      } else {
        setMenuError(result?.message || "Failed to fetch menu items");
      }
    } catch (err) {
      console.error("❌ Error fetching menu items:", err);
      if (err.message.includes("Failed to fetch") || err.message.includes("Network")) {
        setMenuError("Network error. Please check your internet connection.");
      } else {
        setMenuError("Failed to load menu items. Please try again.");
      }
    } finally {
      setMenuLoading(false);
    }
  }, [id]);

  // Load more items (next page)
  const loadMoreItems = useCallback(() => {
    if (menuPagination.currentPage < menuPagination.lastPage && !menuLoading) {
      loadMenuItems(menuPagination.currentPage + 1);
    }
  }, [menuPagination, menuLoading, loadMenuItems]);

  // Update cart from API response
  const updateCartFromResponse = useCallback((cartData) => {
    if (!cartData?.cart?.items) return;

    const transformedItems = cartData.cart.items.map((item) => ({
      id: item.menu_item_id,
      cart_item_id: item.id,
      name: item.menu_name || item.name,
      quantity: item.quantity,
      price: parseFloat(item.unit_price) || 0,
      unit_price: parseFloat(item.unit_price) || 0,
      total_price: parseFloat(item.total_price) || 0,
      image: item.menu_image || item.image || "",
      isVeg: item.variation_name ? item.variation_name.toLowerCase().includes("veg") : false,
      description: item.instructions || "",
    }));

    dispatch(setCartItems(transformedItems));
    return transformedItems;
  }, [dispatch]);

  // Load cart data
  const loadCartData = useCallback(async () => {
    try {
      if (!authToken) {
        console.log("🔑 No auth token, skipping cart load");
        return;
      }

      console.log("📡 Loading cart data...");
      const cartData = await cartService.getCart();
      
      if (cartData?.cart?.items) {
        updateCartFromResponse(cartData);
      } else {
        dispatch(setCartItems([]));
      }
    } catch (error) {
      console.error("❌ Error loading cart:", error);
    }
  }, [authToken, dispatch, updateCartFromResponse]);

  // Direct API call to add/update cart
  const updateCartAPI = useCallback(async (menuId, quantity, itemName) => {
    try {
      const token = authService.getToken();
      if (!token) {
        throw new Error("No authentication token");
      }

      const payload = {
        menu_id: parseInt(menuId),
        quantity: parseInt(quantity)
      };

      console.log(`📤 API Call: ${itemName} -> Menu ID: ${menuId}, Quantity: ${quantity}`, payload);
      console.log(`📡 API URL: ${CART_API_URL}`);

      const response = await fetch(CART_API_URL, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify(payload),
      });

      console.log(`📡 Response Status: ${response.status}`);

      const responseText = await response.text();
      console.log("📥 Raw Response:", responseText);

      let result;
      try {
        result = JSON.parse(responseText);
        console.log("📦 Parsed Response:", result);
      } catch (parseError) {
        console.error("❌ Failed to parse JSON:", parseError);
        throw new Error(`Invalid JSON response: ${responseText}`);
      }

      if (response.ok && result?.data?.status === 200) {
        updateCartFromResponse(result.data);
        return { success: true, data: result.data };
      } else if (result?.data?.status === 409 || result?.status === 409) {
        return { success: false, error: "CART_CONFLICT", message: result?.data?.message || "Cart conflict" };
      } else {
        if (result?.errors) {
          const errorMessages = Object.values(result.errors).flat().join(", ");
          return { 
            success: false, 
            error: "VALIDATION_ERROR",
            message: errorMessages || result?.message || "Validation failed"
          };
        }
        return { 
          success: false, 
          error: result?.data?.message || result?.message || "Failed to update cart" 
        };
      }
    } catch (error) {
      console.error("❌ API Error:", error);
      
      if (error.message.includes("Failed to fetch") || error.message.includes("Network")) {
        return { 
          success: false, 
          error: "Network error",
          message: "Network error. Please check your internet connection."
        };
      }
      
      return { 
        success: false, 
        error: error.message || "Unknown error",
        message: error.message || "Failed to update cart"
      };
    }
  }, [updateCartFromResponse]);

  // Handle add to cart
  const handleAddToCart = useCallback(async (item) => {
    const existingItem = cartItems.find(c => c.id === item.id);
    if (existingItem) {
      handleIncrement(item);
      return;
    }

    if (cartItems.length > 0 && resDetails.id !== restaurantInfo?.id) {
      contextValue.showResCartAlert();
      return;
    }

    if (!authToken) {
      showToast('Please login to add items to cart', 'error');
      return;
    }

    console.log(`🛒 Adding to cart: ${item.name} (ID: ${item.id})`);

    setLoadingItems(prev => ({ ...prev, [item.id]: true }));

    try {
      dispatch(addItems({ 
        id: item.id, 
        name: item.name,
        unit_price: parseFloat(item.unit_price),
        image: item.image,
        isVeg: item.restroType === "veg"
      }));

      const result = await updateCartAPI(item.id, 1, item.name);
      
      if (result.success) {
        showToast(`${item.name} added to cart`, 'success');
      } else if (result.error === "CART_CONFLICT") {
        dispatch(updateItemQuantity({ id: item.id, quantity: 0 }));
        contextValue.showResCartAlert();
        showToast('Cannot add items from different restaurants', 'error');
      } else if (result.error === "Network error") {
        dispatch(updateItemQuantity({ id: item.id, quantity: 0 }));
        showToast('Network error. Please check your connection.', 'error');
      } else if (result.error === "VALIDATION_ERROR") {
        dispatch(updateItemQuantity({ id: item.id, quantity: 0 }));
        showToast(result.message || 'Invalid request. Please try again.', 'error');
      } else {
        dispatch(updateItemQuantity({ id: item.id, quantity: 0 }));
        showToast(result.message || 'Failed to add item', 'error');
      }
    } catch (error) {
      console.error("❌ Error adding to cart:", error);
      dispatch(updateItemQuantity({ id: item.id, quantity: 0 }));
      showToast('Failed to add item. Please try again.', 'error');
    } finally {
      setLoadingItems(prev => ({ ...prev, [item.id]: false }));
    }
  }, [cartItems, resDetails.id, restaurantInfo, dispatch, updateCartAPI, showToast, contextValue, authToken]);

  // Handle increment (+)
  const handleIncrement = useCallback(async (item) => {
    const currentItem = cartItems.find(c => c.id === item.id);
    if (!currentItem) {
      handleAddToCart(item);
      return;
    }

    if (!authToken) {
      showToast('Please login to update cart', 'error');
      return;
    }

    const currentQuantity = currentItem.quantity;
    const newQuantity = currentQuantity + 1;

    console.log(`🔼 Incrementing ${item.name}: ${currentQuantity} -> ${newQuantity}`);

    setLoadingItems(prev => ({ ...prev, [item.id]: true }));

    try {
      dispatch(updateItemQuantity({ id: item.id, quantity: newQuantity }));

      const result = await updateCartAPI(item.id, newQuantity, item.name);
      
      if (result.success) {
        showToast(`${item.name} added to cart`, 'success');
      } else {
        dispatch(updateItemQuantity({ id: item.id, quantity: currentQuantity }));
        
        if (result.error === "Network error") {
          showToast('Network error. Please check your connection.', 'error');
        } else if (result.error === "VALIDATION_ERROR") {
          showToast(result.message || 'Invalid request. Please try again.', 'error');
        } else {
          showToast(result.message || 'Failed to update cart', 'error');
        }
      }
    } catch (error) {
      console.error("❌ Error incrementing item:", error);
      dispatch(updateItemQuantity({ id: item.id, quantity: currentQuantity }));
      showToast('Failed to update cart', 'error');
    } finally {
      setLoadingItems(prev => ({ ...prev, [item.id]: false }));
    }
  }, [cartItems, dispatch, updateCartAPI, showToast, handleAddToCart, authToken]);

  // Handle decrement (-)
  const handleDecrement = useCallback(async (item) => {
    const currentItem = cartItems.find(c => c.id === item.id);
    if (!currentItem || currentItem.quantity <= 0) return;

    if (!authToken) {
      showToast('Please login to update cart', 'error');
      return;
    }

    const currentQuantity = currentItem.quantity;
    const newQuantity = currentQuantity - 1;

    console.log(`🔽 Decrementing ${item.name}: ${currentQuantity} -> ${newQuantity}`);

    setLoadingItems(prev => ({ ...prev, [item.id]: true }));

    try {
      if (newQuantity === 0) {
        dispatch(updateItemQuantity({ id: item.id, quantity: 0 }));
        
        const result = await updateCartAPI(item.id, 0, item.name);
        
        if (result.success) {
          showToast(`${item.name} removed from cart`, 'error');
        } else {
          dispatch(updateItemQuantity({ id: item.id, quantity: currentQuantity }));
          
          if (result.error === "Network error") {
            showToast('Network error. Please check your connection.', 'error');
          } else if (result.error === "VALIDATION_ERROR") {
            showToast(result.message || 'Invalid request. Please try again.', 'error');
          } else {
            showToast(result.message || 'Failed to remove item', 'error');
          }
        }
      } else {
        dispatch(updateItemQuantity({ id: item.id, quantity: newQuantity }));

        const result = await updateCartAPI(item.id, newQuantity, item.name);
        
        if (result.success) {
          showToast(`${item.name} removed from cart`, 'error');
        } else {
          dispatch(updateItemQuantity({ id: item.id, quantity: currentQuantity }));
          
          if (result.error === "Network error") {
            showToast('Network error. Please check your connection.', 'error');
          } else if (result.error === "VALIDATION_ERROR") {
            showToast(result.message || 'Invalid request. Please try again.', 'error');
          } else {
            showToast(result.message || 'Failed to update cart', 'error');
          }
        }
      }
    } catch (error) {
      console.error("❌ Error decrementing item:", error);
      dispatch(updateItemQuantity({ id: item.id, quantity: currentQuantity }));
      showToast('Failed to update cart', 'error');
    } finally {
      setLoadingItems(prev => ({ ...prev, [item.id]: false }));
    }
  }, [cartItems, dispatch, updateCartAPI, showToast, authToken]);

  const getItemQuantity = (itemId) => {
    const item = cartItems.find(c => c.id === itemId);
    return item ? item.quantity : 0;
  };

  // Group menu items by category using restaurantInfo.categories
  const getMenuItemsByCategory = () => {
    if (!menuItems || !restaurantInfo?.categories) return {};
    const grouped = {};
    menuItems.forEach((item) => {
      const categoryIds = item.category_id || [];
      categoryIds.forEach((catId) => {
        const category = restaurantInfo.categories.find((c) => c.id === catId);
        const categoryName = category?.name || "Other";
        if (!grouped[categoryName]) grouped[categoryName] = [];
        grouped[categoryName].push(item);
      });
    });
    return grouped;
  };

  const groupedMenuItems = getMenuItemsByCategory();
  const categoryNames = Object.keys(groupedMenuItems);

  const filteredMenuItems =
    selectedCategory === "all"
      ? menuItems
      : groupedMenuItems[selectedCategory] || [];

  // Load data on mount
  useEffect(() => {
    loadRestaurantInfo();
  }, [id, loadRestaurantInfo]);

  // Load menu items after restaurant info is loaded
  useEffect(() => {
    if (restaurantInfo?.id) {
      loadMenuItems(1);
    }
  }, [restaurantInfo, loadMenuItems]);

  // Load cart after auth
  useEffect(() => {
    if (authToken) {
      loadCartData();
    }
  }, [authToken, loadCartData]);

  // Handle retry
  const handleRetry = () => {
    window.location.reload();
  };

  // Combined loading state
  if (loading) return <RestaurantMenuShimmering />;

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <p className="text-red-500 text-lg mb-4">{error}</p>
          {isNetworkError && (
            <p className="text-gray-600 text-sm mb-4">
              Please check your internet connection and try again.
            </p>
          )}
          <button
            onClick={handleRetry}
            className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!restaurantInfo) return null;

  const { name, address, cuisines, estimated_delivery_time, delivery_charge, opening_time, closing_time, avgRating, countUser, reviews } = restaurantInfo;

  return (
    <MyContext.Provider value={contextValue}>
      <div className="flex justify-center pt-28">
        <div className="container w-[90%] md:w-[70%] lg:w-[50%]">
          {/* Toast notifications */}
          {toast && (
            <Toast 
              message={toast.message} 
              type={toast.type} 
              onClose={hideToast} 
            />
          )}

          {/* Breadcrumb */}
          <h4 className="text-xs text-left text-[#93959f] px-4">
            Home / {city} / {name}
          </h4>

          {/* Restaurant Info */}
          <div className="mx-3">
            <h1 className="font-[700] pt-8 text-2xl">{name}</h1>
            <div className="mt-6 bg-gradient-to-b from-white to-[#dcdce3] p-4 rounded-3xl">
              <div className="flex flex-col mx-auto p-4 rounded-3xl bg-white">
                <div className="flex items-center font-semibold">
                  <FontAwesomeIcon
                    icon={faStar}
                    className="bg-green-700 text-xs text-white p-1 rounded-full"
                  />
                  <h2 className="ml-2 text-lg">
                    {avgRating || "New"} ({countUser || 0}+ ratings)
                  </h2>
                </div>

                <div className="mt-2 text-[#f26618] font-[600]">
                  {cuisines?.map((c) => c.name).join(", ")}
                </div>

                <div className="flex gap-3 items-center mt-2">
                  <div className="flex flex-col justify-center items-center">
                    <div className="h-2 w-2 bg-[#c4c4c4] rounded-full"></div>
                    <div className="h-[1.5rem] bg-[#c4c4c4] w-[0.1rem]"></div>
                    <div className="h-2 w-2 bg-[#c4c4c4] rounded-full"></div>
                  </div>
                  <div>
                    <div className="flex gap-2">
                      <p className="font-semibold">Outlet</p>
                      <p className="text-[#777a7d]">
                        {address?.split(",").slice(-3).join(",").trim()}
                      </p>
                    </div>
                    <div>
                      <p className="font-semibold">
                        Delivery Time: {estimated_delivery_time || "30-40 mins"}
                      </p>
                    </div>
                  </div>
                </div>

                <hr className="border-t-1 border-gray-400 my-3" />

                <div className="flex items-center gap-1.5">
                  <FontAwesomeIcon icon={faMotorcycle} className="text-gray-500" />
                  <p className="font-[500] text-[#848484]">
                    Delivery Charge: ₹{delivery_charge || "0"}
                  </p>
                </div>

                <div className="flex items-center gap-1.5 mt-2">
                  <FontAwesomeIcon icon={faClock} className="text-gray-500" />
                  <p className="font-[500] text-[#848484]">
                    {opening_time} - {closing_time}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Category Filter */}
          {categoryNames.length > 0 && (
            <>
              <hr className="my-8" />
              <div className="px-4 overflow-x-auto">
                <div className="flex gap-3 pb-2">
                  <button
                    onClick={() => setSelectedCategory("all")}
                    className={`px-4 py-2 rounded-full transition-colors whitespace-nowrap ${
                      selectedCategory === "all"
                        ? "bg-orange-500 text-white"
                        : "bg-gray-200 text-gray-700 hover:bg-orange-100"
                    }`}
                  >
                    All Items ({menuItems.length})
                  </button>
                  {categoryNames.map((catName) => (
                    <button
                      key={catName}
                      onClick={() => setSelectedCategory(catName)}
                      className={`px-4 py-2 rounded-full transition-colors whitespace-nowrap ${
                        selectedCategory === catName
                          ? "bg-orange-500 text-white"
                          : "bg-gray-200 text-gray-700 hover:bg-orange-100"
                      }`}
                    >
                      {catName} ({groupedMenuItems[catName]?.length || 0})
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          <p className="py-4 text-center leading-loose">M E N U</p>

          {/* Menu Items */}
          <div className="px-4">
            {filteredMenuItems.length === 0 && !menuLoading ? (
              <div className="text-center py-12">
                <p className="text-gray-500">No items found in this category</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredMenuItems.map((item) => {
                  const quantity = getItemQuantity(item.id);
                  const isInCart = quantity > 0;
                  const isLoading = loadingItems[item.id];

                  return (
                    <div key={item.id} className="py-6 first:pt-0 last:pb-0">
                      <div className="flex justify-between gap-4">
                        {/* Left: Info */}
                        <div className="flex-1">
                          <div className="mb-1">
                            {item.restroType === "veg" ? (
                              <FontAwesomeIcon
                                icon={faCircle}
                                className="border border-solid border-green-600 text-green-600 h-3 w-3 p-0.5 text-xs"
                              />
                            ) : (
                              <FontAwesomeIcon
                                className="border border-solid border-red-500 text-red-500 h-3 w-3 p-0.5 text-xs -rotate-90"
                                icon={faPlay}
                              />
                            )}
                          </div>

                          <h3 className="text-lg font-bold text-gray-800">
                            {item.name}
                          </h3>
                          <p className="text-gray-500 text-sm mt-1 line-clamp-2">
                            {item.description}
                          </p>

                          <div className="mt-2">
                            <span className="text-xl font-bold text-orange-600">
                              ₹{parseFloat(item.final_price || item.unit_price).toFixed(0)}
                            </span>
                            {item.discount_price > 0 && parseFloat(item.discount_price) > 0 && (
                              <>
                                <span className="text-gray-400 line-through ml-2">
                                  ₹{parseFloat(item.discount_price).toFixed(0)}
                                </span>
                                <span className="text-green-600 text-sm ml-2">
                                  Save ₹
                                  {(parseFloat(item.discount_price) - parseFloat(item.final_price || item.unit_price)).toFixed(0)}
                                </span>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Right: Image + Cart controls */}
                        <div className="flex flex-col items-center gap-2">
                          {item.image && (
                            <img
                              src={item.image}
                              alt={item.name}
                              className="w-20 h-20 md:w-24 md:h-24 rounded-lg object-cover"
                              onError={(e) => {
                                e.target.src = "https://via.placeholder.com/96x96?text=No+Image";
                              }}
                            />
                          )}

                          {!isInCart ? (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleAddToCart(item);
                              }}
                              disabled={isLoading || !authToken}
                              className={`px-6 py-2 bg-white border border-green-600 text-green-600 
                                rounded-lg hover:bg-green-50 transition-colors font-semibold text-sm 
                                active:scale-95 transform 
                                ${(isLoading || !authToken) ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              {!authToken ? 'LOGIN TO ADD' : isLoading ? 'ADDING...' : 'ADD'}
                            </button>
                          ) : (
                            <div className="flex items-center gap-2 bg-white border border-green-600 rounded-lg">
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleDecrement(item);
                                }}
                                disabled={isLoading}
                                className={`w-8 h-8 flex items-center justify-center text-green-600 
                                  hover:bg-green-50 rounded-l-lg active:scale-95 transform
                                  ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                              >
                                <FontAwesomeIcon icon={faMinus} />
                              </button>

                              <span className="text-green-600 font-semibold min-w-[20px] text-center select-none">
                                {quantity}
                              </span>

                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleIncrement(item);
                                }}
                                disabled={isLoading}
                                className={`w-8 h-8 flex items-center justify-center text-green-600 
                                  hover:bg-green-50 rounded-r-lg active:scale-95 transform
                                  ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                              >
                                <FontAwesomeIcon icon={faPlus} />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Load More Button */}
            {menuPagination.currentPage < menuPagination.lastPage && (
              <div className="text-center py-6">
                <button
                  onClick={loadMoreItems}
                  disabled={menuLoading}
                  className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {menuLoading ? 'Loading...' : 'Load More Items'}
                </button>
              </div>
            )}
          </div>

          {/* Reviews */}
          {reviews && reviews.length > 0 && (
            <div className="px-4 mt-8 mb-8">
              <h2 className="text-xl font-bold mb-4">Customer Reviews</h2>
              <div className="space-y-4">
                {reviews.map((review) => (
                  <div key={review.id} className="border-b border-gray-100 pb-4 last:border-0">
                    <div className="flex items-start gap-3">
                      <img
                        src={review.userImage}
                        alt={review.user}
                        className="w-10 h-10 rounded-full object-cover"
                        onError={(e) => {
                          e.target.src = "https://via.placeholder.com/40x40?text=User";
                        }}
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <h4 className="font-semibold text-gray-800">{review.user}</h4>
                          <span className="text-sm text-gray-500">{review.created_at}</span>
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                          {[...Array(5)].map((_, i) => (
                            <FontAwesomeIcon
                              key={i}
                              icon={faStar}
                              className={`text-sm ${i < review.rating ? "text-yellow-400" : "text-gray-300"}`}
                            />
                          ))}
                        </div>
                        <p className="text-gray-600 mt-2">{review.review}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {resCartAlert && resetCartShown && <ResetCart />}
      </div>
    </MyContext.Provider>
  );
};

export default RestaurantMenu;